import { createAsyncThunk, isRejected } from '@reduxjs/toolkit';
import { push } from 'connected-react-router';
import { cryptoHelpers } from '@saucelabs/sl-util-helpers';
import Sentry from '@saucelabs/sl-util-sentry/lib';
import { records as appStorageRecords } from '@saucelabs/sl-module-app-storage';

import types from './types';
import { Preset } from './records';
import actions from './actions';
import utils from './utils';
import {
  actions as realDeviceSelectionActions,
  records as realDeviceSelectionRecords,
} from '../realDeviceSelection';
import { actions as testConfigurationActions } from '../testConfiguration';
import { handleTabsFormSubmit } from '../../actions';
import { CONFIGURATION_DEVICE_TYPES } from '../../constants';

const getPresetsWithMetadata = createAsyncThunk(
  types.PRESETS_GET_WITH_METADATA,
  async (noArgument, { dispatch, rejectWithValue }) => {
    try {
      const fetchPresetsAction = await dispatch(actions.fetchPresets());
      if (isRejected(fetchPresetsAction)) {
        throw fetchPresetsAction;
      }
      const presetItems = fetchPresetsAction.payload.items;

      if (presetItems.length > 0) {
        const appIds: string[] = presetItems
          .map((serverPreset) => new Preset(serverPreset).getAppId())
          .filter(Boolean);

        if (appIds.length !== 0) {
          const fetchAppsAction = await dispatch(actions.fetchAppStorageFiles(appIds));

          if (isRejected(fetchAppsAction)) {
            throw fetchAppsAction;
          }
        }
      }

      // as weird as the following looks, I couldn't find another possibility
      // than defer the execution to the next event loop
      // otherwise any useEffect can't detect dependency changes as intended
      await new Promise((resolve) => setTimeout(resolve, 10));

      return {};
    } catch (error: any) { // eslint-disable-line @typescript-eslint/no-explicit-any
      // as weird as the following looks, I couldn't find another possibility
      // than defer the execution to the next event loop
      // otherwise any useEffect can't detect dependency changes as intended
      await new Promise((resolve) => setTimeout(resolve, 10));

      return rejectWithValue(error.payload ?? {});
    }
  },
);

const prepareAndStoreRecentPreset = createAsyncThunk(
  types.PRESETS_PREPARE_AND_STORE_RECENT,
  async (capabilities: Record<string, unknown>, { dispatch, rejectWithValue }) => {
    try {
      const sanitizedCapabilities = utils.sanitizeCapabilities(capabilities);
      const hash = await cryptoHelpers.createHash(sanitizedCapabilities);

      const createOrUpdateRecentPresetResponse = await dispatch(actions.createOrUpdateRecentPreset({
        hash,
        payload: sanitizedCapabilities,
        payload_version: 1,
      }));

      // TODO: remove once we load recent presets on "page load"
      // since that would serve exactly the same purpose
      // and directly return "createOrUpdateRecentPresetResponse"
      void dispatch(getPresetsWithMetadata());

      return createOrUpdateRecentPresetResponse;
    } catch (error: any) { // eslint-disable-line @typescript-eslint/no-explicit-any
      Sentry.captureException(error.payload ?? error);
      return rejectWithValue(error.payload ?? error);
    }
  },
);

const startSessionFromPreset = (
  preset: Preset,
  app: appStorageRecords.AppStorageFile | null,
) => (
  async (dispatch: ReduxDispatcher): Promise<void> => {
    const payload = {
      ...preset.payload,
      mobileAppConfiguration: app ?? null,
    };

    if (preset.payload.deviceType === CONFIGURATION_DEVICE_TYPES.REAL_DEVICE) {
      await dispatch(realDeviceSelectionActions.fetchMobileSessionAvailable());
      payload.realDeviceConfiguration = new realDeviceSelectionRecords.DeviceDescriptor(
        preset.payload.realDeviceConfiguration,
      );
    }

    dispatch(testConfigurationActions.updateTestConfigValue(payload));

    // Navigate to the correct URL to avoid unexpected behavior
    dispatch(push(utils.getConfigUrlForPreset(preset)));

    void dispatch(handleTabsFormSubmit());
  }
);

export default {
  getPresetsWithMetadata,
  prepareAndStoreRecentPreset,
  startSessionFromPreset,
};
